import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { PdfResponse } from 'types/pdf';
import { DisplayConfig } from 'containers/Programmations/ProgV2/atoms';
import { shortenUUID } from 'utils/shortenUUID';
import { PrintConfig } from 'containers/Programmations/ProgV2/Preview/types';
import { buildProgPDFParams } from 'containers/Programmations/ProgV2/Preview/Sidebar/buildProgPDFParams';
import { ProgrammationPDFSettings } from 'types/programmations';

interface Props {
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  programmationId: string;
  programmationName: string;
  displayConfig?: DisplayConfig;
  printConfig: PrintConfig;
  settings: ProgrammationPDFSettings;
  hideTitle: boolean;
}

const fetchProgrammationAPI = (s3Url: string, programmationName: string) =>
  fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${programmationName}.pdf`);
    });

async function downloadProgrammation(props: Props) {
  const {
    schoolyearId,
    schoolId,
    programmationId,
    programmationName,
    displayConfig,
    printConfig,
    hideTitle,
    settings,
    onStart,
    onFinished,
    onError,
  } = props;

  onStart();

  const url = new URL(
    `${
      import.meta.env.VITE_API_URL
    }/schoolyears/${schoolyearId}/schools/${schoolId}/programmations/${programmationId}/generate`,
  );

  const hiddenPeriodes = displayConfig?.hiddenPeriodes
    .map(shortenUUID)
    .join(',');
  const hiddenDomaines = displayConfig?.hiddenDomaines
    .map(shortenUUID)
    .join(',');
  const matiereIds = printConfig?.matiereIds.map(shortenUUID).join(',');

  const params = buildProgPDFParams({
    hiddenPeriodes: hiddenPeriodes || '',
    hiddenDomaines: hiddenDomaines || '',
    printConfig,
    matiereIds,
    hideTitle,
    settings,
  });

  if (printConfig?.preventCutting !== undefined) {
    params.append('preventCutting', printConfig?.preventCutting?.toString());
  }

  url.search = params.toString();

  try {
    const response: PdfResponse = await request(url.toString(), {
      method: 'GET',
    });
    const token = response.pdf_token;
    await fetchProgrammationAPI(token, programmationName);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadProgrammation() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['programmation']);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const download = ({
    programmationName,
    programmationId,
    displayConfig,
    printConfig,
    settings,
    hideTitle,
  }: {
    programmationId: string;
    programmationName: string;
    displayConfig: DisplayConfig;
    printConfig: PrintConfig;
    settings: ProgrammationPDFSettings;
    hideTitle: boolean;
  }) =>
    downloadProgrammation({
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      programmationId: programmationId,
      programmationName: programmationName,
      displayConfig,
      printConfig,
      settings,
      hideTitle,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () =>
        notifyError({
          title: t('programmation:messages.programmation.dowloadError'),
        }),
    });

  return {
    download,
    isDownloading,
  };
}
